/**
 * A caixa de diálogo do miolo de Finanças, sobre o `Dialog` do Radix.
 *
 * Papel: reproduz `.modal-backdrop`, `.modal`, `.modal-hd`, `.modal-body` e
 * `.modal-ft` (l. 113–119) — véu na janela inteira, caixa alinhada ao topo a
 * 40 px, raio de 12 px, até 90 % da altura com rolagem interna, cabeçalho e
 * rodapé em `--superficie-2` —, com o "✕" de `.btn-ghost.btn-sm.btn-icon`. O
 * Radix dá o foco preso e o `aria-modal`.
 *
 * Como usar: quem monta a caixa a monta só quando ela está aberta, e a desmonta
 * para fechar. `onFechar` é chamado pelos três caminhos da origem: o "✕"; o
 * clique no véu, só quando o botão do mouse desceu **e** subiu no próprio véu
 * (l. 720) — quem seleciona o texto de um campo e solta o botão fora da caixa
 * não a fecha —; e `Esc`, que fecha **todas** as caixas abertas de uma vez
 * (l. 5464–5466), porque cada uma ouve a tecla por conta própria no
 * `document`. O "Cancelar" do `rodape` é de quem monta.
 *
 * A caixa nasce num portal no fim do `<body>`, fora do `QuadroEmbutido`, e por
 * isso o véu leva a classe do tema: sem ela, a borda, o véu e a sombra sairiam
 * com os valores do `:root`.
 */
import { Dialog as DialogPrimitive } from 'radix-ui';
import { type MouseEvent, type ReactNode, useEffect, useRef } from 'react';

import { CLASSE_DO_TEMA, useTemaEmbutido } from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import { Botao } from './botao';

/**
 * As larguras da origem: as conferências de importação, o lançamento e a
 * calculadora, os cadastros e o perfil, a conferência do salário, e a menor.
 */
export type LarguraDaCaixa = 720 | 640 | 460 | 440 | 360;

/** A classe de cada largura máxima. */
const CLASSE_DA_LARGURA: Readonly<Record<LarguraDaCaixa, string>> = {
  720: 'max-w-[720px]',
  640: 'max-w-[640px]',
  460: 'max-w-[460px]',
  440: 'max-w-[440px]',
  360: 'max-w-[360px]',
};

interface CaixaDeDialogoProps {
  /** O título, que é também o nome acessível da caixa. */
  titulo: string;
  largura: LarguraDaCaixa;
  /** Chamado pelo "✕", pelo clique no véu e por `Esc`. */
  onFechar: () => void;
  /** Os botões do rodapé, na ordem da origem. */
  rodape: ReactNode;
  /** O corpo. */
  children: ReactNode;
}

/**
 * A caixa de diálogo, aberta enquanto montada.
 *
 * @param titulo - O texto do cabeçalho, em Fraunces 17 px 500.
 * @param largura - A largura máxima da caixa, em px.
 * @param onFechar - Chamado pelos três jeitos de fechar.
 * @param rodape - Os botões, alinhados à direita com 8 px entre eles.
 * @param children - O corpo, com 18 px de respiro.
 * @returns O véu com a caixa.
 */
export function CaixaDeDialogo({
  titulo,
  largura,
  onFechar,
  rodape,
  children,
}: CaixaDeDialogoProps) {
  const tema = useTemaEmbutido();
  const desceuNoVeu = useRef(false);
  const fechar = useRef(onFechar);

  useEffect(() => {
    fechar.current = onFechar;
  });

  useEffect(() => {
    /**
     * Fecha a caixa com `Esc`, como todas as outras abertas fecham.
     *
     * @param evento - A tecla.
     */
    function handleTecla(evento: KeyboardEvent) {
      if (evento.key === 'Escape') fechar.current();
    }
    document.addEventListener('keydown', handleTecla);
    return () => document.removeEventListener('keydown', handleTecla);
  }, []);

  /**
   * Guarda se o botão do mouse desceu no próprio véu.
   *
   * @param evento - O `mousedown` no véu ou em algo dentro dele.
   */
  function handleMouseDown(evento: MouseEvent<HTMLDivElement>) {
    desceuNoVeu.current = evento.target === evento.currentTarget;
  }

  /**
   * Fecha quando o clique começou e terminou no véu.
   *
   * @param evento - O `click` no véu ou em algo dentro dele.
   */
  function handleClique(evento: MouseEvent<HTMLDivElement>) {
    if (evento.target === evento.currentTarget && desceuNoVeu.current) onFechar();
  }

  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          onMouseDown={handleMouseDown}
          onClick={handleClique}
          className={cn(
            CLASSE_DO_TEMA[tema],
            'fixed inset-0 z-[100] flex items-start justify-center bg-emb-veu px-4 pt-10 pb-4',
          )}
        >
          <DialogPrimitive.Content
            aria-describedby={undefined}
            onEscapeKeyDown={(evento) => evento.preventDefault()}
            onPointerDownOutside={(evento) => evento.preventDefault()}
            onInteractOutside={(evento) => evento.preventDefault()}
            className={cn(
              'max-h-[90vh] w-full overflow-y-auto rounded-[12px] bg-superficie shadow-emb-modal outline-none',
              CLASSE_DA_LARGURA[largura],
            )}
          >
            <div className="flex items-center justify-between border-b border-emb-borda bg-superficie-2 px-[18px] py-3.5">
              <DialogPrimitive.Title asChild>
                <h3 className="m-0 font-serif text-[17px] font-medium text-foreground">
                  {titulo}
                </h3>
              </DialogPrimitive.Title>
              <Botao
                variante="fantasma"
                tamanho="sm"
                icone
                aria-label="Fechar"
                onClick={onFechar}
              >
                ✕
              </Botao>
            </div>
            <div className="p-[18px]">{children}</div>
            <div className="flex justify-end gap-2 border-t border-emb-borda bg-superficie-2 px-[18px] py-3">
              {rodape}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
